import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, from, of, pipe, Observable } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  auditTime,
  distinctUntilChanged,
  map,
  mergeMap,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  CombinedLongeingDataClass,
  DbStatus,
  LongeingDirectionEnum,
  LongeingDirectionType,
  Recording,
  RecordingClass,
  RecordingStatusEnum,
  RecordingUpdate,
} from '@sleipai/sleip-models';
import { HorseService } from '../../horses-store/services/horse.service';
import { UserService } from '../../user-store/services/user.service';
import { documentId, FieldPath, OrderByDirection } from 'firebase/firestore';
import {
  QueryConfig,
  PAGE_SIZE,
  filterReocringsList,
  BatchService,
} from '../../helpers';
import {
  collection,
  collectionSnapshots,
  doc,
  docSnapshots,
  endAt,
  Firestore,
  getCountFromServer,
  limit,
  orderBy,
  query,
  startAfter,
  startAt,
  updateDoc,
  where,
  writeBatch,
  DocumentSnapshot,
  Timestamp,
  or,
} from '@angular/fire/firestore';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Query } from '@angular/fire/firestore';
import { isEqual } from 'lodash';
import { arrayUnion } from '@angular/fire/firestore';
import { arrayRemove } from '@angular/fire/firestore';

@Injectable()
export class RecordingService {
  constructor() { }

  private destroyRef = inject(DestroyRef);

  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private horseService = inject(HorseService);
  private userService = inject(UserService);
  private batchService = inject(BatchService);

  private nexPageOffset = new BehaviorSubject<any | null>(null);

  private lastDocument: DocumentSnapshot | null = null;

  getRecordingById(id: string) {
    const recordingRef = doc(this.firestore, `recordings/${id}`);
    return docSnapshots(recordingRef).pipe(
      takeUntilDestroyed(this.destroyRef),
      auditTime(100),
      map((rec) => {
        return new RecordingClass({
          ...rec.data(),
          id: rec.id,
        });
      })
    );
  }

  getRecordingsStatus(status: string, dbStatus: string) {
    const recordingsRef = collection(this.firestore, 'recordings');
    const q = query(
      recordingsRef,
      where('status', '==', status),
      where('dbStatus', '==', dbStatus),
      orderBy('creationDate', 'desc'),
      limit(500)
    );
    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.map((recording) => {
          return new RecordingClass({
            ...recording.data(),
            id: recording.id,
          });
        });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  getAllRecordings() {
    const recordingsRef = collection(this.firestore, 'recordings');
    const q = query(
      recordingsRef,
      where('mimirAnalysis', '==', true),
      orderBy('creationDate', 'desc')
    );
    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.map((recording) => {
          return new RecordingClass({
            id: recording.id,
            ...recording.data(),
          });
        });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  getPagedRecording(status: string, dbStatus: string, offset: any) {
    const queryConstraints = [];

    if (dbStatus !== 'removed') {
      queryConstraints.push(where('status', '==', status));
    }

    queryConstraints.push(
      where('dbStatus', '==', dbStatus),
      orderBy('creationDate', 'desc'),
      limit(10)
    );

    if (offset) {
      queryConstraints.push(startAfter(offset));
    }

    const recordingsRef = collection(this.firestore, 'recordings');
    const q = query(recordingsRef, ...queryConstraints);

    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.reduce((acc, cur) => {
          const id = cur.id;
          const data = new RecordingClass({
            ...cur.data(),
            id: id,
          });
          return [...acc, data];
        }, Array.from<RecordingClass>([]));
      })
    );
  }

  getRecordingsRemoved(offset: number) {
    const recordingsRef = collection(this.firestore, 'recordings');
    let q = query(
      recordingsRef,
      where('dbStatus', '==', 'removed'),
      orderBy('creationDate', 'desc'),
      limit(10)
    );
    if (offset) {
      q = query(q, startAfter(offset));
    }
    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.map((recording) => {
          return new RecordingClass({
            ...recording.data(),
            id: recording.id,
          });
        });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  getAnalysisByHorseId(id: string) {
    const recordingsRef = collection(this.firestore, 'recordings');
    const q = query(recordingsRef, where('horseId', '==', id));
    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.map((recording) => {
          return new RecordingClass({
            ...recording.data(),
            id: recording.id,
          });
        });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  getAnalysisByUserIds(ids: string[]) {
    const recordingsRef = collection(this.firestore, 'recordings');
    let q = query(recordingsRef);
    if (ids && ids.length > 0) {
      q = query(q, where('uploadedBy', 'in', ids));
    }

    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.map((recording) => {
          return new RecordingClass({
            ...recording.data(),
            id: recording.id,
          });
        });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  getRecordingsWithComment(dates: any) {
    const recordingsRef = collection(this.firestore, 'recordings');
    const q = query(
      recordingsRef,
      where('dbStatus', '==', 'active'),
      orderBy('creationDate', 'desc'),
      startAt(dates.endDate),
      endAt(dates.startDate)
    );
    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.map((recording) => {
          return new RecordingClass({
            ...recording.data(),
            id: recording.id,
          });
        });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  updateRecording(id: any, update: object) {
    const updatingRecordingRef = doc(this.firestore, `recordings/${id}`);
    return updateDoc(updatingRecordingRef, update).then(() =>
      console.log(`Updated recording ${id}`)
    );
  }

  getActiveRecordingsBasedOnDate(dates: any) {
    const recordingsRef = collection(this.firestore, 'recordings');
    const q = query(
      recordingsRef,
      where('status', '==', 'complete'),
      where('dbStatus', '==', 'active'),
      orderBy('creationDate', 'desc'),
      startAt(dates.endDate),
      endAt(dates.startDate)
    );
    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.map((recording) => {
          return new RecordingClass({
            ...recording.data(),
            id: recording.id,
          });
        });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  getTodaysRecordings() {
    const date = new Date();
    const midnight = new Date(date.setHours(0, 0, 0, 0));
    const recordingsRef = collection(this.firestore, 'recordings');
    const q = query(
      recordingsRef,
      where('dbStatus', '==', 'active'),
      orderBy('creationDate', 'desc'),
      where('creationDate', '>', midnight)
    );
    return collectionSnapshots(q).pipe(
      map((snap) => {
        return snap.map((recording) => {
          return new RecordingClass({
            ...recording.data(),
            id: recording.id,
          });
        });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  updateRecordingOwner(recording: any) {
    // Get horse
    const owner = this.horseService.getSingleHorse(recording.horseId).pipe(
      take(1),
      switchMap((horse: any) => {
        // Get owner
        return this.userService.getUser(horse.owner).pipe(
          map((user) => {
            const toSave = {
              owner: {
                name: user.name,
                id: user.uid,
              },
              userType: user.userStatus,
            };
            if (recording.firestoreId) {
              return this.updateRecording(recording.firestoreId, toSave);
            } else {
              return this.updateRecording(recording.id, toSave);
            }
          })
        );
      })
    );

    return owner;
  }

  getRecordingsByIds(ids: string[]) {
    return this.batchService
      .queryByArrayValues<Recording>('recordings', 'firestoreId', ids)
      .pipe(
        map((dataList) => {
          return dataList.map((recording) => {
            return new RecordingClass(recording);
          });
        }),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef),
      );
  }

  // Render video data:
  getRenderVideoData(id: string) {
    return docSnapshots(
      doc(this.firestore, `recordings/${id}/renderVideoData/dataSet`)
    ).pipe(
      map((rec) => {
        return {
          ...rec.data(),
          id: rec.id,
        } as any;
      })
    );
  }

  getAnalysisStrideData(id: string) {
    return docSnapshots(doc(this.firestore, `stride-analysis/${id}`)).pipe(
      map((rec) => {
        return {
          ...rec.data(),
          id: rec.id,
        } as any;
      })
    );
  }

  getRecordingsListInFirestoreDb(queryConfig: QueryConfig, horseId?: string) {
    const {
      page,
      orderBy = 'creationDate',
      orderDirection = 'asc',
      pageSize = PAGE_SIZE,
    } = queryConfig;
    const offset = page * 20;

    const recordingsRef = collection(this.firestore, 'recordings');
    let recordingQuery = this.getRecordingListPaginationQuery(
      recordingsRef,
      horseId,
      orderBy,
      orderDirection
    );
    recordingQuery = query(recordingQuery, limit(offset ? offset : pageSize));
    return collectionSnapshots(recordingQuery)
      .pipe(
        pipe(takeUntilDestroyed(this.destroyRef)),
        map((arr) => {
          if (arr[arr.length - 1] && arr[arr.length - 1])
            return arr[arr.length - 1];
          return null;
        })
      )
      .pipe(
        mergeMap((arr) => {
          if (!arr) return of([]);
          const recordingsRef = collection(this.firestore, 'recordings');
          let recordingQuery = this.getRecordingListPaginationQuery(
            recordingsRef,
            horseId,
            orderBy,
            orderDirection
          );
          if (page === 0) {
            recordingQuery = query(recordingQuery, limit(pageSize));
          }
          recordingQuery = query(
            recordingQuery,
            startAfter(arr),
            limit(pageSize)
          );

          return collectionSnapshots(recordingQuery).pipe(
            map((arr) => {
              if (!arr || arr.length <= 0) return [];
              return arr.reduce((acc, cur) => {
                const id = cur.id;
                const data = new RecordingClass({
                  ...cur.data(),
                  id: id,
                });
                return [...acc, data];
              }, Array.from<RecordingClass>([]));
            })
          );
        })
      );
  }

  getRecordingsCountInFirestoreDb(
    horseId?: string,
    filterMap?: Map<string, any>
  ) {
    let recordingCollectionRef = query(
      collection(this.firestore, 'recordings')
    );
    if (horseId) {
      recordingCollectionRef = query(
        recordingCollectionRef,
        where('horseId', '==', horseId)
      );
    }
    recordingCollectionRef = query(
      recordingCollectionRef,
      where('dbStatus', '==', DbStatus.ACTIVE)
    );

    const hasAnalgesia = filterMap?.get('hasAnalgesia');
    if (hasAnalgesia) {
      recordingCollectionRef = query(
        recordingCollectionRef,
        where('analgesia.hasAnalgesia', '==', hasAnalgesia)
      );
    }

    const analysisTypesFilter = filterReocringsList(filterMap);
    if (analysisTypesFilter && analysisTypesFilter.length > 0) {
      recordingCollectionRef = query(
        recordingCollectionRef,
        where('analysisType', 'in', analysisTypesFilter)
      );
    }

    return from(getCountFromServer(recordingCollectionRef)).pipe(
      takeUntilDestroyed(this.destroyRef)
    );
  }

  removeRecording(recordingId: string) {
    const recordingRef = doc(this.firestore, `recordings/${recordingId}`);
    return updateDoc(recordingRef, {
      dbStatus: 'removed',
    });
  }

  // Infinite recording list pagination start
  fetchRecordingsInfiniteScrollListPageForHorse(
    horseId: string,
    queryConfig: QueryConfig
  ) {
    const {
      orderBy = 'creationDate',
      orderDirection = 'desc',
      pageSize = PAGE_SIZE,
      page,
      filterMap,
    } = queryConfig;
    const queryPageOffset = page === 0 ? null : this.nexPageOffset.value;
    const recordingsRef = collection(this.firestore, 'recordings');
    let recordingQuery = this.getRecordingListPaginationQuery(
      recordingsRef,
      horseId,
      orderBy,
      orderDirection,
      filterMap
    );
    recordingQuery = query(
      recordingQuery,
      startAfter(queryPageOffset || 0),
      limit(pageSize)
    );
    return collectionSnapshots(recordingQuery).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((snap) => {
        if (!snap || snap.length <= 0) return Array.from<Recording>([]);

        const recordingList = snap.reduce((acc, cur) => {
          const id = cur.id;
          const data = new RecordingClass({
            ...cur.data(),
            id,
          });
          return [...acc, data];
        }, Array.from<Recording>([]));
        const nexPageOffset = snap[snap.length - 1];
        this.nexPageOffset.next(nexPageOffset);
        return recordingList;
      })
    );
  }

  //// Dashboard 2.0 recordings list pagination start ////
  fetchRecordingsListForInfiniteScrollInDashBoard(
    reset: boolean,
    queryConfig: QueryConfig
  ) {
    const { pageSize = PAGE_SIZE } = queryConfig;
    const recordingsRef = collection(this.firestore, 'recordings');

    let recordingsQuery = this.getRecordingsQueryForInfiniteScrollInDashBoard(
      recordingsRef,
      reset,
      queryConfig
    );

    recordingsQuery = query(recordingsQuery, limit(pageSize));

    return collectionSnapshots(recordingsQuery).pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      map((snap) => {
        if (!snap || snap.length <= 0)
          return {
            recordings: Array.from<Recording>([]),
            hasMore: false,
          };

        const recordings = snap.reduce((acc, cur) => {
          const id = cur.id;
          const data = new RecordingClass({
            ...cur.data(),
            id,
          });
          return [...acc, data];
        }, Array.from<Recording>([]));

        this.lastDocument = snap[snap.length - 1];
        const hasMore = snap.length === pageSize;
        return {
          recordings,
          hasMore,
        };
      })
    );
  }

  fetchRecordingsCountForInfiniteScrollInDashBoard(
    reset: boolean,
    queryConfig: QueryConfig
  ) {
    const recordingsRef = collection(this.firestore, 'recordings');
    const recordingsQuery = this.getRecordingsQueryForInfiniteScrollInDashBoard(
      recordingsRef,
      reset,
      queryConfig
    );
    return from(getCountFromServer(recordingsQuery)).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((result) => result.data().count)
    );
  }

  private getRecordingsQueryForInfiniteScrollInDashBoard(
    queryRef: Query,
    reset: boolean,
    queryConfig: QueryConfig
  ) {
    const {
      orderBy: orderByField = 'creationDate',
      orderDirection = 'desc',
      filterMap,
    } = queryConfig;
    let recordingsQuery = query(queryRef);

    const allRecordings = filterMap?.get('all');
    const horseId = filterMap?.get('horseId');
    const horseOwnerUserId = filterMap?.get('horseOwnerUserId');
    const recordingUploadedBy = filterMap?.get('recordingUploadedBy');

    if (horseId) {
      recordingsQuery = query(recordingsQuery, where('horseId', '==', horseId));
    }

    if (recordingUploadedBy) {
      recordingsQuery = query(
        recordingsQuery,
        where(new FieldPath('uploadedBy'), '==', recordingUploadedBy)
      );
    }
    if (!allRecordings) {
      const isRemoved = filterMap?.get('removed');
      if (isRemoved) {
        recordingsQuery = query(
          recordingsQuery,
          where('dbStatus', '==', DbStatus.REMOVED)
        );
      }

      const hasAnalgesia = filterMap?.get('hasAnalgesia');
      if (hasAnalgesia) {
        recordingsQuery = query(
          recordingsQuery,
          where(new FieldPath('analgesia', 'hasAnalgesia'), '==', true)
        );
      }

      const analysisTypesFilter = filterReocringsList(filterMap);
      if (analysisTypesFilter && analysisTypesFilter.length > 0) {
        recordingsQuery = query(
          recordingsQuery,
          where('analysisType', 'in', analysisTypesFilter)
        );
      }

      const remote = filterMap?.get('remote');
      if (remote && horseOwnerUserId) {
        recordingsQuery = query(
          recordingsQuery,
          where('uploadedBy', '!=', horseOwnerUserId)
        );
      }
    }

    recordingsQuery = query(
      recordingsQuery,
      orderBy(orderByField ?? 'creationDate', orderDirection)
    );

    if (reset) {
      this.lastDocument = null;
    } else {
      recordingsQuery = query(recordingsQuery, startAfter(this.lastDocument));
    }
    return recordingsQuery;
  }
  //// Dashboard 2.0 recordings list pagination end ////

  // Infinite recording list pagination end

  updateRecordingData(updatedRecordingData: RecordingUpdate) {
    const recordingRef = doc(
      this.firestore,
      `recordings/${updatedRecordingData.id}`
    );

    const descriptionUpdate =
      typeof updatedRecordingData.description === 'string'
        ? { description: updatedRecordingData.description.trim() }
        : {};

    const analgesiaLocations = updatedRecordingData.currentAnalgesiaLocations;
    const analgesiaUpdate = Array.isArray(analgesiaLocations)
      ? {
          'analgesia.hasAnalgesia': analgesiaLocations.length > 0,
          'analgesia.locations': analgesiaLocations.map((tag) => ({
            ...tag,
          })),
        }
      : {};

    const surfaceTag = updatedRecordingData.currentSurfaceTag;
    const surfaceTagUpdate =
      surfaceTag !== undefined
        ? {
            'tags.surface': surfaceTag ? { [surfaceTag.id]: { ...surfaceTag } } : {},
          }
        : {};

    return updateDoc(recordingRef, {
      ...descriptionUpdate,
      ...analgesiaUpdate,
      ...surfaceTagUpdate,
    });
  }

  fetchEligibleRecordingsToLinkForARecording(
    horseId: string,
    recordingId: string,
    creationDate: Date,
    longeingDirection: LongeingDirectionType
  ) {
    const recordingsRef = collection(this.firestore, 'recordings');
    const oneDayInMs = 24 * 60 * 60 * 1000;
    const startDate = new Date(creationDate.getTime() - oneDayInMs);
    const endDate = new Date(creationDate.getTime() + oneDayInMs);
    // Convert to Firestore Timestamps
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    let recordingsQuery = query(
      recordingsRef,
      where(documentId(), '!=', recordingId),
      where('horseId', '==', horseId),
      where('dbStatus', '==', DbStatus.ACTIVE),
      where('status', '==', RecordingStatusEnum.COMPLETE)
    );

    if (longeingDirection === LongeingDirectionEnum.CLOCKWISE) {
      recordingsQuery = query(
        recordingsQuery,
        or(
          where(
            new FieldPath('longeingData', 'direction'),
            '==',
            LongeingDirectionEnum.COUNTER_CLOCKWISE
          ),
          where(
            new FieldPath('longeingData', 'direction'),
            '==',
            LongeingDirectionEnum.ANTI_CLOCKWISE
          )
        )
      );
    } else {
      recordingsQuery = query(
        recordingsQuery,
        where(
          new FieldPath('longeingData', 'direction'),
          '==',
          LongeingDirectionEnum.CLOCKWISE
        )
      );
    }
    recordingsQuery = query(
      recordingsQuery,
      orderBy('creationDate', 'desc'),
      where('creationDate', '>', startTimestamp),
      where('creationDate', '<', endTimestamp)
    );

    return collectionSnapshots(recordingsQuery).pipe(
      take(1),
      map((snap) => {
        if (!snap || snap.length <= 0) return Array.from<Recording>([]);

        const recordingList = snap.reduce((acc, cur) => {
          const id = cur.id;
          const data = new RecordingClass({
            ...cur.data(),
            id,
          });
          return [...acc, data];
        }, Array.from<Recording>([]));
        return recordingList;
      })
    );
  }

  updateCombinedViewLinkedRecording(
    recordingId: string,
    newLinkedRecordingId: string | null,
    previousLinkedRecordingId: string | null
  ) {
    const batch = writeBatch(this.firestore);
    const currentRef = doc(this.firestore, `recordings/${recordingId}`);
    const selfItem = { id: recordingId, type: 'longeing' };

    const newlyLinkedItem = newLinkedRecordingId
      ? { id: newLinkedRecordingId, type: 'longeing' }
      : null;

    // 1. Update current recording
    const currentUpdate: any = {};
    if (previousLinkedRecordingId && newlyLinkedItem) {
      currentUpdate['linkedRecordings.sources'] = [newlyLinkedItem];
      currentUpdate['linkedRecordings.targets'] = [newlyLinkedItem];
      currentUpdate['longeingData.linkedRecordingId'] = newLinkedRecordingId;
    } else if (previousLinkedRecordingId) {
      const prevItem = { id: previousLinkedRecordingId, type: 'longeing' };
      currentUpdate['linkedRecordings.sources'] = arrayRemove(prevItem);
      currentUpdate['linkedRecordings.targets'] = arrayRemove(prevItem);
      currentUpdate['longeingData.linkedRecordingId'] = null;
    } else if (newlyLinkedItem) {
      currentUpdate['linkedRecordings.sources'] = arrayUnion(newlyLinkedItem);
      currentUpdate['linkedRecordings.targets'] = arrayUnion(newlyLinkedItem);
      currentUpdate['longeingData.linkedRecordingId'] = newLinkedRecordingId;
    } else {
      currentUpdate['longeingData.linkedRecordingId'] = null;
    }
    batch.update(currentRef, currentUpdate);

    // 2. If there was a previous linked recording, unlink it
    if (
      previousLinkedRecordingId &&
      previousLinkedRecordingId !== newLinkedRecordingId
    ) {
      const prevRef = doc(
        this.firestore,
        `recordings/${previousLinkedRecordingId}`
      );
      batch.update(prevRef, {
        'linkedRecordings.sources': arrayRemove(selfItem),
        'linkedRecordings.targets': arrayRemove(selfItem),
        'longeingData.linkedRecordingId': null,
      });
    }

    // 3. If there is a new linked recording, link it back to current recording
    if (
      newLinkedRecordingId &&
      newLinkedRecordingId !== previousLinkedRecordingId
    ) {
      const newRef = doc(this.firestore, `recordings/${newLinkedRecordingId}`);
      batch.update(newRef, {
        'linkedRecordings.sources': arrayUnion(selfItem),
        'linkedRecordings.targets': arrayUnion(selfItem),
        'longeingData.linkedRecordingId': recordingId,
      });
    }

    return batch.commit();
  }

  removeUserFromGroup(groupId: string, userToRemove: any) {
    const removedUser = userToRemove ? { ...userToRemove } : {};
    const groupRef = doc(this.firestore, `groups/${groupId}`);
    return updateDoc(groupRef, {
      previousUsers: arrayUnion(removedUser),
    }).then(() => {
      updateDoc(groupRef, {
        users: arrayRemove(removedUser),
      });
    });
  }

  batchRemoveRecordings(recordingIds: string[]) {
    const callable = httpsCallable(this.functions, 'recordingsApi-removeRecordings');
    return from(callable({ recordingIds })).pipe(
      map((data) => data.data as { success: boolean; removedCount: number; failedIds: string[] } | null)
    );
  }

  batchMoveRecordingsToHorse(recordingIds: string[], horseId: string) {
    const callable = httpsCallable(this.functions, 'horsesApi-batchMoveRecordingsToHorse');
    return from(callable({ recordingIds, horseId })).pipe(
      map((data) => data.data as { failedIds: string[] } | null)
    );
  }

  fetchCombinedLongeingsByHorseId(horseId: string): Observable<CombinedLongeingDataClass[]> {
    const combinedLongeingColRef = collection(
      this.firestore,
      `horses/${horseId}/combinedLongeing`
    );
    return collectionSnapshots(combinedLongeingColRef).pipe(
      map((snapshots) => {
        return snapshots.map(
          (snap) =>
            new CombinedLongeingDataClass({
              ...snap.data(),
              combinedId: snap.id,
            })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private getRecordingListPaginationQuery(
    queryRef: Query,
    horseId?: string,
    orderByField?: string,
    orderDirection: OrderByDirection = 'asc',
    filterMap?: Map<string, any>
  ) {
    if (horseId) queryRef = query(queryRef, where('horseId', '==', horseId));
    const analysisTypesFilter = filterReocringsList(filterMap);
    if (analysisTypesFilter && analysisTypesFilter.length > 0) {
      queryRef = query(
        queryRef,
        where('analysisType', 'in', analysisTypesFilter)
      );
    }
    const hasAnalgesia = filterMap?.get('hasAnalgesia');
    if (hasAnalgesia) {
      queryRef = query(
        queryRef,
        where(new FieldPath('analgesia', 'hasAnalgesia'), '==', hasAnalgesia)
      );
    }
    return query(
      queryRef,
      where('dbStatus', '==', DbStatus.ACTIVE),
      orderBy('dbStatus', 'asc'),
      orderBy(orderByField ?? 'creationDate', orderDirection)
    );
  }
}
